// Full-Page Project Edit Wizard (React + Tailwind)
import React, { useState, useEffect } from 'react';
import { Upload, Trash, Loader, ChevronRight, ChevronLeft, FileText, MapPin, Info, Download } from 'lucide-react';
import { useParams } from 'react-router-dom';
import api from '../../axios';

const initialDocuments = {
  architectural_design: null,
  structural_design: null,
  electrical_design: null,
  mechanical_design: null,
  soil_report: null,
  eia_report: null,
  site_analysis_report: null,
  title_document: null,
  affidavit: null,
  power_of_attorney: null,
  aprn_certificate: null,
};

const ProjectEditWizardPage = () => {
  const { id } = useParams();
  const [step, setStep] = useState(1);
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    location: '',
    documents: { ...initialDocuments },
  });
  const [existingDocs, setExistingDocs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await api.get(`/projects/${id}`);
  
        const {
          name,
          description,
          location,
          documents
        } = response.data;
  
        setProjectData((prev) => ({
          ...prev,
          name,
          description,
          location,
          documents: { ...initialDocuments } // for new uploads only
        }));
  
        // Decode stringified JSON if needed
        const parsedDocs = typeof documents === 'string'
          ? JSON.parse(documents)
          : documents;
  
        setExistingDocs(parsedDocs || {});
      } catch (err) {
        console.error('Failed to fetch project', err);
      }
    };
  
    fetchProject();
  }, [id]);
  

  const handleInputChange = (e) => {
    setProjectData({ ...projectData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    setProjectData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [key]: file,
      },
    }));
  };

  const renderUploadField = (label, key) => {
    const existingFile = existingDocs[key]?.at(-1);
    const currentFile = projectData.documents[key];

    return (
      <div className="mb-6 relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

        {!currentFile ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors cursor-pointer bg-gray-50 hover:bg-gray-100">
            <div className="flex flex-col items-center justify-center">
              <Upload className="w-10 h-10 text-blue-500 mb-2" />
              <p className="text-sm font-medium text-gray-700">Drag and drop your file here or</p>
              <label className="mt-2 cursor-pointer">
                <span className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors inline-block text-sm font-medium">Browse Files</span>
                <input 
                  type="file" 
                  accept=".pdf,.ifc" 
                  onChange={(e) => handleFileChange(e, key)} 
                  className="hidden" 
                />
              </label>
              <p className="text-xs text-gray-500 mt-2">Supported formats: PDF, IFC</p>
            </div>
            {existingFile && (
              <div className="mt-4 flex items-center text-sm text-gray-700">
                <FileText className="w-5 h-5 text-green-600 mr-2" />
                <a
                  href={`/storage/${existingFile.path}`}
                  download
                  className="text-blue-600 hover:underline flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download existing file
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-blue-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-800">{currentFile.name}</p>
                  <p className="text-xs text-gray-500">{Math.round(currentFile.size / 1024)} KB</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleFileChange({ target: { files: [] } }, key)}
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
              >
                <Trash className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };



  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
              <Info className="w-5 h-5 mr-2 text-blue-500" />
              Project Information
            </h2>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter project name"
                value={projectData.name}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                name="description"
                placeholder="Describe your project"
                value={projectData.description}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all min-h-32"
              ></textarea>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <div className="flex items-center border border-gray-300 rounded-lg p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
                <MapPin className="w-5 h-5 text-gray-400 mr-2" />
                <input
                  type="text"
                  name="location"
                  placeholder="Coordinates or address"
                  value={projectData.location}
                  onChange={handleInputChange}
                  className="w-full outline-none"
                />
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-500" />
              Design Documents
            </h2>
            {renderUploadField("Architectural Design", "architectural_design")}
            {renderUploadField("Structural Design", "structural_design")}
            {renderUploadField("Electrical Design", "electrical_design")}
            {renderUploadField("Mechanical Design", "mechanical_design")}
          </>
        );
      case 3:
        return (
          <>
            <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-500" />
              Reports
            </h2>
            {renderUploadField("Soil Test Report", "soil_report")}
            {renderUploadField("Environmental Impact Assessment (EIA)", "eia_report")}
            {renderUploadField("Site Analysis Report (SAR)", "site_analysis_report")}
          </>
        );
      case 4:
        return (
          <>
            <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-500" />
              Legal & Ownership Documents
            </h2>
            {renderUploadField("Title Document (C of O / R of O)", "title_document")}
            {renderUploadField("Affidavit Affirming Ownership", "affidavit")}
            {renderUploadField("Registered Power of Attorney", "power_of_attorney")}
            {renderUploadField("APRN Certificate", "aprn_certificate")}
          </>
        );
      case 5:
        return (
          <>
            <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-500" />
              Review & Submit
            </h2>
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
              <h3 className="font-medium text-gray-700 mb-2">Project Details</h3>
              <div className="mb-4 pl-4 border-l-2 border-blue-400">
                <p className="text-sm"><span className="font-medium">Name:</span> {projectData.name}</p>
                <p className="text-sm"><span className="font-medium">Location:</span> {projectData.location}</p>
              </div>

              <h3 className="font-medium text-gray-700 mb-2">Updated Documents</h3>
              <div className="pl-4 border-l-2 border-green-400 space-y-2">
                {Object.entries(projectData.documents).map(([key, file]) =>
                  file ? (
                    <div key={key} className="flex items-center">
                      <FileText className="w-4 h-4 text-green-500 mr-2" />
                      <p className="text-sm text-gray-700">
                        {key.replace(/_/g, ' ')} <span className="text-xs text-gray-500">({file.name})</span>
                      </p>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('name', projectData.name);
    formData.append('description', projectData.description);
    formData.append('location', projectData.location);
    for (const key in projectData.documents) {
      if (projectData.documents[key]) {
        formData.append(`documents[${key}]`, projectData.documents[key]);
      }
    }

    try {
      await api.post(`/projects/${id}/update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });
      alert('Project updated successfully!');
    } catch (error) {
      console.error(error);
      alert('Update failed!');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 md:px-12">
      <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-lg">
        <div className="flex items-center justify-between mb-8 border-b pb-4">
          <h1 className="text-2xl font-bold text-gray-800">Edit Project</h1>
          <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
            Step {step} of 5
          </div>
        </div>

        {/* Step Progress Bar */}
        <div className="flex justify-between mb-10 relative">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex flex-col items-center relative z-10">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step > s ? 'bg-green-500 text-white' : step === s ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s ? '✓' : s}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {s === 1 ? 'Info' : s === 2 ? 'Design' : s === 3 ? 'Reports' : s === 4 ? 'Legal' : 'Review'}
              </div>
            </div>
          ))}
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 -z-10"></div>
          <div
            className="absolute top-4 left-0 h-0.5 bg-blue-500 -z-5 transition-all duration-300"
            style={{ width: `${(step - 1) * 25}%` }}
          ></div>
        </div>

        <div className="mt-6">{renderStepContent()}</div>

        {isSubmitting && (
          <div className="mt-8">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">Uploading: {uploadProgress}%</p>
          </div>
        )}

        <div className="mt-10 flex justify-between">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="px-6 py-3 flex items-center text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center font-medium shadow-sm"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-6 py-3 ${
                isSubmitting ? 'bg-green-400' : 'bg-green-500 hover:bg-green-600'
              } text-white rounded-lg transition-colors flex items-center font-medium shadow-sm`}
            >
              {isSubmitting ? (
                <>
                  <Loader className="animate-spin w-4 h-4 mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Update Project
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


export default ProjectEditWizardPage;
